import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  Users,
  CreditCard,
  Video,
  Plus,
  Building2,
  Coins,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  UserPlus,
  Trash2,
  Copy,
  Check,
  ArrowUpRight,
  Wallet,
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  Globe
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BuyCreditsModal from '../../components/BuyCreditsModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [showAllocateCredits, setShowAllocateCredits] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [createdPartnerPassword, setCreatedPartnerPassword] = useState(null);
  const [createdPartnerEmail, setCreatedPartnerEmail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // CSV import states
  const [csvFile, setCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  
  const [newPartner, setNewPartner] = useState({ name: '', email: '', phone: '', country: '' });
  const [allocateCredits, setAllocateCredits] = useState({ 
    credits: 0, 
    message: '', 
    source_type: 'personal',
    source_org_id: null 
  });
  const [allocatedCreditsBreakdown, setAllocatedCreditsBreakdown] = useState([]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/agent/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocatedCreditsBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/api/agent/allocated-credits-breakdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllocatedCreditsBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching allocated credits breakdown:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchAllocatedCreditsBreakdown();
    }
  }, [token]);

  // CSV Import Functions
  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(`${API}/api/agent/partner-agents/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data.results);
      toast.success(response.data.message);
      fetchDashboard();
      fetchAllocatedCreditsBreakdown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "name,email,credits,source_type\nJohn Doe,john@example.com,5,personal\nJane Smith,jane@example.com,10,personal";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'partner_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetImportDialog = () => {
    setShowImportCSV(false);
    setCsvFile(null);
    setImportResults(null);
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    if (!newPartner.name || !newPartner.email || !newPartner.country) {
      toast.error('Please fill name, email, and country');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/api/agent/partner-agents`, newPartner, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      if (response.data.is_new_user && response.data.temp_password) {
        setCreatedPartnerPassword(response.data.temp_password);
        setCreatedPartnerEmail(newPartner.email);
      }
      
      setNewPartner({ name: '', email: '', phone: '', country: '' });
      setShowAddPartner(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add partner agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocateCredits = async (e) => {
    e.preventDefault();
    if (allocateCredits.credits <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    
    if (allocateCredits.source_type === 'allocated' && !allocateCredits.source_org_id) {
      toast.error('Please select an organization for allocated credits');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/api/agent/partner-agents/${selectedPartner.id}/allocate-credits`,
        {
          credits: allocateCredits.credits,
          message: allocateCredits.message,
          source_type: allocateCredits.source_type,
          source_org_id: allocateCredits.source_org_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowAllocateCredits(false);
      setAllocateCredits({ credits: 0, message: '', source_type: 'personal', source_org_id: null });
      setSelectedPartner(null);
      fetchDashboard();
      fetchAllocatedCreditsBreakdown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemovePartner = async (partnerId, partnerName) => {
    if (!window.confirm(`Are you sure you want to remove ${partnerName} as your partner? Their allocated credits will be returned to you.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/api/agent/partner-agents/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove partner');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine which view to show based on route
  const currentPath = location.pathname;
  const showPartnerAgentsView = currentPath.includes('/partner-agents');
  const showInterviewsView = currentPath.includes('/interviews');
  const showApplicationsView = currentPath.includes('/applications');

  if (loading) {
    return (
      <DashboardLayout role="agent">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="agent">
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Welcome, {user?.name}</h2>
            <p className="text-muted-foreground">Manage your partner agents and interviews</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowImportCSV(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowAddPartner(true)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Partner
            </Button>
            <Button 
              className="bg-lime-500 hover:bg-lime-600 text-white"
              onClick={() => setShowBuyCredits(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Buy Credits
            </Button>
          </div>
        </div>

        {/* Organization Info (if linked) */}
        {dashboardData?.organization && (
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Organization</p>
                  <p className="font-heading font-semibold text-lg">{dashboardData.organization.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Allocated Credits</p>
                  <p className="text-2xl font-bold text-amber-500">{dashboardData.organization.allocated_credits}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parent Agents Info (if any) */}
        {dashboardData?.parent_agents?.length > 0 && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Credits from Partner Relationships</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {dashboardData.parent_agents.map((parent) => (
                  <div key={parent.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className="text-sm">{parent.name}</span>
                    <Badge variant="outline" className="text-purple-500">
                      {parent.allocated_credits} credits
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credits Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Personal Credits</p>
                  <p className="text-3xl font-heading font-bold text-lime-500">{dashboardData?.personal_credits || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Available to use or distribute</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Allocated Credits</p>
                  <p className="text-3xl font-heading font-bold text-amber-500">{dashboardData?.allocated_credits || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">From orgs & partners</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Consumed Credits</p>
                  <p className="text-3xl font-heading font-bold text-red-500">{dashboardData?.consumed_credits || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Used for interviews</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Partner Agents</p>
                  <p className="text-3xl font-heading font-bold text-purple-500">{dashboardData?.total_partner_agents || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Interviews</p>
                  <p className="text-3xl font-heading font-bold">{dashboardData?.total_interviews || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">{dashboardData?.completed_interviews || 0} completed</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Video className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Partner Agents Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Partner Agents
                </CardTitle>
                <CardDescription>Manage your partner agent network and distribute credits</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchDashboard}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {dashboardData?.partner_agents?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Personal Credits</TableHead>
                    <TableHead className="text-right">Allocated by You</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.partner_agents.map((partner) => (
                    <TableRow key={partner.id}>
                      <TableCell className="font-medium">{partner.name}</TableCell>
                      <TableCell>{partner.email}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="text-lime-500">
                          {partner.personal_credits}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-amber-500/10 text-amber-500 hover:bg-amber-500/20">
                          {partner.allocated_credits}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPartner(partner);
                              setShowAllocateCredits(true);
                            }}
                          >
                            <Coins className="w-4 h-4 mr-1" />
                            Allocate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => handleRemovePartner(partner.id, partner.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 border rounded-lg border-dashed">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Partner Agents Yet</h3>
                <p className="text-muted-foreground mb-4">Add partner agents to expand your network and distribute credits</p>
                <Button onClick={() => setShowAddPartner(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Your First Partner
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Interviews */}
        {dashboardData?.interviews?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Recent Interviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>University</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Agent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.interviews.slice(0, 5).map((interview) => (
                    <TableRow key={interview.id}>
                      <TableCell className="font-medium">{interview.student_name || interview.applicant_name}</TableCell>
                      <TableCell>{interview.university_name}</TableCell>
                      <TableCell>
                        <Badge variant={interview.status === 'completed' ? 'default' : 'secondary'}>
                          {interview.status === 'completed' ? (
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</>
                          ) : interview.status === 'in_progress' ? (
                            <><Clock className="w-3 h-3 mr-1" /> In Progress</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {interview.overall_result === 'pass' ? (
                          <Badge className="bg-green-500/10 text-green-500">Pass</Badge>
                        ) : interview.overall_result === 'fail' ? (
                          <Badge className="bg-red-500/10 text-red-500">Fail</Badge>
                        ) : (
                          <Badge variant="outline">-</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {interview.agent_name || 'Self'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Partner Dialog */}
      <Dialog open={showAddPartner} onOpenChange={setShowAddPartner}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Partner Agent</DialogTitle>
            <DialogDescription>
              Add an existing agent or create a new one as your partner. You can allocate credits to them afterwards.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPartner} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="Partner agent name"
                value={newPartner.name}
                onChange={(e) => setNewPartner({...newPartner, name: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="partner@example.com"
                value={newPartner.email}
                onChange={(e) => setNewPartner({...newPartner, email: e.target.value})}
                required
              />
              <p className="text-xs text-muted-foreground">If this email already exists, they will be added as your partner</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                placeholder="+1234567890"
                value={newPartner.phone}
                onChange={(e) => setNewPartner({...newPartner, phone: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country" className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Country *
              </Label>
              <select
                id="country"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={newPartner.country}
                onChange={(e) => setNewPartner({...newPartner, country: e.target.value})}
                required
              >
                <option value="">Select Country</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="United States">United States</option>
                <option value="India">India</option>
                <option value="Pakistan">Pakistan</option>
                <option value="Bangladesh">Bangladesh</option>
                <option value="Nigeria">Nigeria</option>
                <option value="Ghana">Ghana</option>
                <option value="Kenya">Kenya</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Italy">Italy</option>
                <option value="Spain">Spain</option>
                <option value="Netherlands">Netherlands</option>
                <option value="China">China</option>
                <option value="Japan">Japan</option>
                <option value="South Korea">South Korea</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Singapore">Singapore</option>
                <option value="Philippines">Philippines</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Thailand">Thailand</option>
                <option value="Vietnam">Vietnam</option>
                <option value="Nepal">Nepal</option>
                <option value="Sri Lanka">Sri Lanka</option>
                <option value="South Africa">South Africa</option>
                <option value="Egypt">Egypt</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddPartner(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-lime-500 hover:bg-lime-600">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Add Partner
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Allocate Credits Dialog */}
      <Dialog open={showAllocateCredits} onOpenChange={setShowAllocateCredits}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Allocate Credits</DialogTitle>
            <DialogDescription>
              Allocate credits to {selectedPartner?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAllocateCredits} className="space-y-4">
            {/* Credit Source Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Credit Source</Label>
              <RadioGroup
                value={allocateCredits.source_type}
                onValueChange={(value) => setAllocateCredits({
                  ...allocateCredits, 
                  source_type: value,
                  source_org_id: value === 'personal' ? null : allocateCredits.source_org_id,
                  credits: 0
                })}
                className="space-y-2"
              >
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${allocateCredits.source_type === 'personal' ? 'border-lime-500 bg-lime-50 dark:bg-lime-950/30' : 'border-border'}`}>
                  <RadioGroupItem value="personal" id="personal" />
                  <Label htmlFor="personal" className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-lime-600" />
                        <span>Personal Credits</span>
                      </div>
                      <Badge variant="outline" className="bg-lime-100 text-lime-700">
                        {dashboardData?.personal_credits || 0} available
                      </Badge>
                    </div>
                  </Label>
                </div>
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${allocateCredits.source_type === 'allocated' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-border'}`}>
                  <RadioGroupItem value="allocated" id="allocated" disabled={allocatedCreditsBreakdown.length === 0} />
                  <Label htmlFor="allocated" className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span>Organization Allocated Credits</span>
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-700">
                        {allocatedCreditsBreakdown.reduce((sum, o) => sum + o.available_credits, 0)} available
                      </Badge>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Organization Selection (if allocated credits selected) */}
            {allocateCredits.source_type === 'allocated' && (
              <div className="space-y-2">
                <Label htmlFor="org-select">Select Organization</Label>
                {allocatedCreditsBreakdown.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                    No allocated credits available from any organization.
                  </p>
                ) : (
                  <Select
                    value={allocateCredits.source_org_id || ""}
                    onValueChange={(value) => setAllocateCredits({...allocateCredits, source_org_id: value, credits: 0})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {allocatedCreditsBreakdown.map((org) => (
                        <SelectItem key={org.org_id} value={org.org_id}>
                          <div className="flex items-center justify-between gap-4">
                            <span>{org.org_name}</span>
                            <Badge variant="secondary" className="ml-2">
                              {org.available_credits} credits
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {allocateCredits.source_org_id && (
                  <p className="text-xs text-blue-600">
                    Partner agent will use these credits only for interviews with this organization.
                  </p>
                )}
              </div>
            )}

            {/* Credits Amount */}
            <div className="space-y-2">
              <Label htmlFor="allocate-credits">Credits to Allocate</Label>
              <Input
                id="allocate-credits"
                type="number"
                min="1"
                max={
                  allocateCredits.source_type === 'personal' 
                    ? (dashboardData?.personal_credits || 0)
                    : (allocatedCreditsBreakdown.find(o => o.org_id === allocateCredits.source_org_id)?.available_credits || 0)
                }
                value={allocateCredits.credits}
                onChange={(e) => setAllocateCredits({...allocateCredits, credits: parseInt(e.target.value) || 0})}
                required
              />
              <div className="flex gap-2 flex-wrap">
                {[5, 10, 25, 50].map((amount) => {
                  const maxAvailable = allocateCredits.source_type === 'personal' 
                    ? (dashboardData?.personal_credits || 0)
                    : (allocatedCreditsBreakdown.find(o => o.org_id === allocateCredits.source_org_id)?.available_credits || 0);
                  return (
                    <Button
                      key={amount}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={amount > maxAvailable}
                      onClick={() => setAllocateCredits({...allocateCredits, credits: Math.min(amount, maxAvailable)})}
                    >
                      +{amount}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Available: {
                  allocateCredits.source_type === 'personal' 
                    ? (dashboardData?.personal_credits || 0)
                    : (allocatedCreditsBreakdown.find(o => o.org_id === allocateCredits.source_org_id)?.available_credits || 0)
                } credits
              </p>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message (optional)</Label>
              <Input
                id="message"
                placeholder="Reason for allocation"
                value={allocateCredits.message}
                onChange={(e) => setAllocateCredits({...allocateCredits, message: e.target.value})}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAllocateCredits(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={
                  submitting || 
                  allocateCredits.credits <= 0 || 
                  (allocateCredits.source_type === 'allocated' && !allocateCredits.source_org_id)
                } 
                className="bg-amber-500 hover:bg-amber-600"
              >
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Coins className="w-4 h-4 mr-2" />}
                Allocate {allocateCredits.credits} Credits
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created Partner Credentials Dialog */}
      <Dialog open={!!createdPartnerPassword} onOpenChange={() => setCreatedPartnerPassword(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              Partner Agent Created!
            </DialogTitle>
            <DialogDescription>
              A new account has been created. Share these credentials with your partner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50 space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-mono text-sm">{createdPartnerEmail}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Temporary Password</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-sm font-bold">{createdPartnerPassword}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(createdPartnerPassword)}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              A welcome email has been sent to the partner with login instructions.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedPartnerPassword(null)} className="bg-lime-500 hover:bg-lime-600">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        open={showBuyCredits} 
        onOpenChange={setShowBuyCredits}
        onSuccess={() => {
          fetchDashboard();
          setShowBuyCredits(false);
        }}
      />

      {/* CSV Import Dialog */}
      <Dialog open={showImportCSV} onOpenChange={resetImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-lime-500" />
              Import Partner Agents via CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk create or link partner agents and optionally allocate credits
            </DialogDescription>
          </DialogHeader>
          
          {!importResults ? (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <Download className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900 dark:text-blue-100">Download Template</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Get the CSV template with required columns: name, email, credits (optional), source_type (optional)
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={downloadTemplate}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label>Select CSV File</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="hidden"
                  />
                  {csvFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="w-8 h-8 text-lime-500" />
                      <div className="text-left">
                        <p className="font-medium">{csvFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(csvFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setCsvFile(null)}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground mb-2">Drop your CSV file here or</p>
                      <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        Browse Files
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions */}
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-100">CSV Format</p>
                    <ul className="mt-2 space-y-1 text-amber-700 dark:text-amber-300">
                      <li>• <strong>name</strong> - Partner agent's name (required)</li>
                      <li>• <strong>email</strong> - Partner agent's email (required)</li>
                      <li>• <strong>credits</strong> - Credits to allocate (optional, from your personal credits)</li>
                      <li>• <strong>source_type</strong> - "personal" or "allocated" (optional, defaults to personal)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Import Results */
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-green-500/10 text-center">
                  <p className="text-2xl font-bold text-green-600">{importResults.created?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Created</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResults.linked?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Linked</p>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 text-center">
                  <p className="text-2xl font-bold text-red-600">{importResults.failed?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Failed</p>
                </div>
              </div>

              {/* Show created partners with credentials */}
              {importResults.created?.length > 0 && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="font-medium text-green-900 dark:text-green-100 mb-2">New Partners Created:</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {importResults.created.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white/50 dark:bg-black/20 rounded">
                        <div>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-muted-foreground ml-2">({item.email})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Pass:</span>
                          <code className="font-mono text-xs bg-muted px-1 rounded">{item.temp_password}</code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(item.temp_password);
                              toast.success('Password copied!');
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show linked partners */}
              {importResults.linked?.length > 0 && (
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">Existing Agents Linked:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.linked.map((item, idx) => (
                      <div key={idx} className="text-sm p-2 bg-white/50 dark:bg-black/20 rounded flex justify-between">
                        <span>{item.name} ({item.email})</span>
                        {item.credits_allocated > 0 && (
                          <Badge variant="outline">{item.credits_allocated} credits allocated</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show failed imports */}
              {importResults.failed?.length > 0 && (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="font-medium text-red-900 dark:text-red-100 mb-2">Failed Imports:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.failed.map((item, idx) => (
                      <div key={idx} className="text-sm p-2 bg-white/50 dark:bg-black/20 rounded">
                        <span className="font-mono">{item.email}</span>
                        <span className="text-red-600 ml-2">- {item.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total credits allocated */}
              {importResults.total_credits_allocated > 0 && (
                <div className="text-center p-3 bg-amber-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Credits Allocated</p>
                  <p className="text-xl font-bold text-amber-600">{importResults.total_credits_allocated}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {!importResults ? (
              <>
                <Button variant="outline" onClick={resetImportDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCSVImport} 
                  disabled={!csvFile || importing}
                  className="bg-lime-500 hover:bg-lime-600"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Partners
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={resetImportDialog} className="bg-lime-500 hover:bg-lime-600">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
